// Self-contained 2D-canvas renderer for the activity HR/power charts, plus the
// pure viewport math that drives zoom/pan on a shared time window. Follows the
// playerChart.js pattern (no G2/@ant-design/plots): a canvas redraws each
// <=2400-point decimated series in milliseconds and stays crisp on resize.

import { axisTicks, formatTimeTick } from './playerChart'

export const ACTIVITY_CHART_COLORS = {
    hr: '#ff4d4f',
    power: '#ffb703',
    grid: '#3a3a3a',
    axis: '#393939',
    label: '#a7a7a7',
    marker: '#ffd31a',
}

export const ACTIVITY_CHART_MIN_SPAN = 30

// Plot-area inset, shared by the renderer and the pointer→time mapping so a
// click/zoom anchor lands exactly on the drawn line (axis padding excluded).
export const ACTIVITY_CHART_PAD = { top: 10, right: 14, bottom: 26, left: 46 }

// Map a trace to a renderable series for one metric: relative seconds since the
// first point, dropping points where the metric is null.
export function activitySeries(trace, key) {
    if (!Array.isArray(trace) || trace.length === 0) return []
    const t0 = trace[0].timestamp
    const out = []
    for (const p of trace) {
        if (p[key] != null) out.push({ time: p.timestamp - t0, value: p[key] })
    }
    return out
}

export function fullViewport(duration) {
    return { start: 0, end: Math.max(0, Number(duration) || 0) }
}

// Normalize a viewport to valid bounds: span clamped to [min(MIN_SPAN,
// duration), max(1, duration)] and start to [0, duration - span].
export function clampViewport(view, options = {}) {
    const duration = Math.max(1, Number(options.duration) || 0)
    const minSpan = options.minSpan != null ? options.minSpan : ACTIVITY_CHART_MIN_SPAN
    let start = Number(view && view.start) || 0
    let end = Number(view && view.end) || duration
    let span = end - start
    const minS = Math.min(minSpan, duration)
    const maxS = Math.max(1, duration)
    span = Math.min(maxS, Math.max(minS, span))
    start = Math.min(Math.max(0, start), Math.max(0, duration - span))
    return { start, end: start + span }
}

// Zoom around `anchorTime` (the time under the cursor stays fixed). factor > 1
// zooms out, < 1 zooms in.
export function zoomViewport(view, options = {}) {
    const duration = Math.max(1, Number(options.duration) || 0)
    const minSpan = options.minSpan != null ? options.minSpan : ACTIVITY_CHART_MIN_SPAN
    const cur = clampViewport(view, { duration, minSpan })
    const span = cur.end - cur.start
    const anchor = Math.min(
        cur.end,
        Math.max(cur.start, options.anchorTime != null ? Number(options.anchorTime) : (cur.start + cur.end) / 2),
    )
    const factor = Number(options.factor)
    let newSpan = isFinite(factor) && factor > 0 ? span * factor : span
    const minS = Math.min(minSpan, duration)
    const maxS = Math.max(1, duration)
    newSpan = Math.min(maxS, Math.max(minS, newSpan))
    const ratio = span > 0 ? (anchor - cur.start) / span : 0
    const newStart = anchor - ratio * newSpan
    return clampViewport({ start: newStart, end: newStart + newSpan }, { duration, minSpan })
}

// Shift the window by deltaSeconds (positive = the view moves forward in time).
export function panViewport(view, options = {}) {
    const duration = Math.max(1, Number(options.duration) || 0)
    const minSpan = options.minSpan != null ? options.minSpan : ACTIVITY_CHART_MIN_SPAN
    const cur = clampViewport(view, { duration, minSpan })
    const delta = Number(options.deltaSeconds) || 0
    return clampViewport({ start: cur.start + delta, end: cur.end + delta }, { duration, minSpan })
}

export function isFullView(view, duration) {
    if (!view || !duration) return true
    return (view.end - view.start) >= duration - 0.5
}

export function formatViewportRange(view) {
    if (!view) return ''
    const start = formatTimeTick(view.start)
    const end = formatTimeTick(view.end)
    if (start === '' || end === '') return ''
    return `${start} – ${end}`
}

// Nearest sample to `time` (by absolute time distance). Used to look up the
// exact HR/power values at a checkpoint even after the series was decimated
// for rendering.
export function nearestSample(series, time) {
    if (!Array.isArray(series) || series.length === 0) return null
    let best = null
    let bestDist = Infinity
    for (const p of series) {
        const d = Math.abs(p.time - time)
        if (d < bestDist) {
            bestDist = d
            best = p
        }
    }
    return best
}

// Map a pointer's clientX onto the view's time axis using the SAME coordinate
// space the renderer plots in (the plot area between the axis insets). Keeps
// the checkpoint line and the wheel-zoom anchor exactly under the cursor.
export function timeAtX(rect, clientX, view) {
    if (!rect || !rect.width || !view) return null
    const span = Math.max(1, view.end - view.start)
    const plotW = Math.max(1, rect.width - ACTIVITY_CHART_PAD.left - ACTIVITY_CHART_PAD.right)
    const t = view.start + ((clientX - rect.left - ACTIVITY_CHART_PAD.left) / plotW) * span
    return Math.min(view.end, Math.max(view.start, t))
}

export function renderActivityChart(canvas, {
    series = [], color = ACTIVITY_CHART_COLORS.power, view, yMax, yMin = 0,
    checkpointTime = null, checkpointLabel = null,
}) {
    const ctx = canvas.getContext && canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1
    const w = Math.max(1, Math.round(rect.width))
    const h = Math.max(1, Math.round(rect.height))
    const bw = Math.round(w * dpr)
    const bh = Math.round(h * dpr)
    if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw
        canvas.height = bh
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    const pad = ACTIVITY_CHART_PAD
    const plotL = pad.left
    const plotR = w - pad.right
    const plotT = pad.top
    const plotB = h - pad.bottom
    const plotW = Math.max(1, plotR - plotL)
    const plotH = Math.max(1, plotB - plotT)

    const vStart = view && view.start != null ? Number(view.start) : 0
    const vEnd = view && view.end != null ? Number(view.end) : Math.max(1, vStart + 1)
    const span = Math.max(1, vEnd - vStart)
    const yMaxV = yMax != null && yMax > 0 ? yMax : 50
    const yMinV = yMin != null && yMin < yMaxV ? Number(yMin) : 0
    const yRange = Math.max(1, yMaxV - yMinV)
    const toX = t => plotL + ((t - vStart) / span) * plotW
    const toY = v => plotB - ((v - yMinV) / yRange) * plotH

    ctx.font = '11px -apple-system, "Segoe UI", Roboto, sans-serif'

    // Y grid + labels (fixed to the full-ride range)
    const yticks = axisTicks(yRange, 6).ticks
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    for (const t of yticks) {
        const yy = toY(yMinV + t)
        ctx.strokeStyle = ACTIVITY_CHART_COLORS.grid
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(plotL, yy)
        ctx.lineTo(plotR, yy)
        ctx.stroke()
        ctx.fillStyle = ACTIVITY_CHART_COLORS.label
        ctx.fillText(String(Math.round(yMinV + t)), plotL - 7, yy)
    }

    // X axis line + labels (absolute times within the visible window)
    const xticks = axisTicks(span, 8).ticks
    ctx.strokeStyle = ACTIVITY_CHART_COLORS.axis
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(plotL, plotB)
    ctx.lineTo(plotR, plotB)
    ctx.stroke()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    for (const t of xticks) {
        const abs = vStart + t
        const xx = toX(abs)
        ctx.beginPath()
        ctx.moveTo(xx, plotB)
        ctx.lineTo(xx, plotB + 4)
        ctx.stroke()
        ctx.fillStyle = ACTIVITY_CHART_COLORS.label
        ctx.fillText(formatTimeTick(abs), xx, plotB + 6)
    }

    // Data polyline (a dot for a single point — no line to draw otherwise)
    if (series.length > 1) {
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(toX(series[0].time), toY(series[0].value))
        for (let i = 1; i < series.length; i++) ctx.lineTo(toX(series[i].time), toY(series[i].value))
        ctx.stroke()
    } else if (series.length === 1) {
        const p = series[0]
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(toX(p.time), toY(p.value), 3, 0, Math.PI * 2)
        ctx.fill()
    }

    // Checkpoint: a shared vertical line with the exact HR/PWR values at the
    // selected timepoint in a small tooltip box next to it.
    if (checkpointTime != null && checkpointTime >= vStart && checkpointTime <= vEnd) {
        const xx = toX(checkpointTime)
        ctx.strokeStyle = ACTIVITY_CHART_COLORS.marker
        ctx.lineWidth = 1.5
        if (ctx.setLineDash) ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(xx, plotT)
        ctx.lineTo(xx, plotB)
        ctx.stroke()
        if (ctx.setLineDash) ctx.setLineDash([])

        if (checkpointLabel) {
            const labelW = ctx.measureText ? ctx.measureText(checkpointLabel).width : 100
            const boxW = labelW + 14
            const boxH = 18
            const gap = 6
            let boxX = xx + gap
            if (boxX + boxW > plotR - gap) boxX = xx - gap - boxW
            boxX = Math.max(plotL + gap, Math.min(boxX, plotR - gap - boxW))
            const boxY = plotT + 4
            ctx.fillStyle = 'rgba(0, 0, 0, 0.78)'
            ctx.strokeStyle = ACTIVITY_CHART_COLORS.label
            ctx.lineWidth = 1
            ctx.fillRect(boxX, boxY, boxW, boxH)
            ctx.strokeRect(boxX, boxY, boxW, boxH)
            ctx.fillStyle = ACTIVITY_CHART_COLORS.label
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(checkpointLabel, boxX + 7, boxY + boxH / 2)
        }
    }
}